// src/pages/personal/analytics/DashboardSection/hooks/useDashboardData.ts
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBusinessId } from '@/hooks/useBusinessId';
import { api } from '@/services/api';
import { DateTime } from 'luxon'; // Добавляем импорт Luxon
import { 
  Period, 
  DateRange,
  MetricType, 
  DashboardMetrics, 
  ChartDataPoint,
  Metric,
  PeakHour,
  PeakDay,
  PlatformItem
} from '../../types/types';
import { processChartData } from '../../utils/chartDataProcessor';

interface UseDashboardDataReturn {
  metrics: DashboardMetrics | null;
  chartData: ChartDataPoint[];
  period: Period;
  selectedMetric: MetricType;
  isLoading: boolean;
  error: string | null;
  rawData: any[]; // Для отладки
  dateRange?: DateRange;
  // Новые данные
  peakHours: PeakHour[];
  peakDays: PeakDay[];
  platformDistribution: PlatformItem[];
  businessTimezone: string;
  // Методы
  setPeriod: (period: Period) => void;
  setSelectedMetric: (metric: MetricType) => void;
  setDateRange: (range: DateRange) => void;
  refreshDashboard: () => Promise<void>;
}

export const useDashboardData = (): UseDashboardDataReturn => {
  const { t } = useTranslation(['dashboard', 'errors']);
  const { businessId, isLoading: isBusinessIdLoading } = useBusinessId();
  
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [rawData, setRawData] = useState<any[]>([]);
  const [period, setPeriod] = useState<Period>('day');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('revenue');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Новые состояния для компонентов
  const [peakHours, setPeakHours] = useState<PeakHour[]>([]);
  const [peakDays, setPeakDays] = useState<PeakDay[]>([]);
  const [platformDistribution, setPlatformDistribution] = useState<PlatformItem[]>([]);
  const [businessTimezone, setBusinessTimezone] = useState<string>('UTC');

  // Флаг для контроля активности компонента
  const isMounted = useRef(true);
  // Флаг для отслеживания первого рендера
  const isFirstRender = useRef(true);
  // Предыдущее состояние параметров для сравнения
  const prevParams = useRef({ period, selectedMetric, dateRange, businessId });
  // Счетчик запросов для отладки
  const fetchCounter = useRef(0);

  // Функция сравнения объектов для определения изменений
  const areParamsEqual = (prev: any, curr: any) => {
    // Проверка базовых полей
    if (prev.period !== curr.period) {
      console.log('Изменился period:', prev.period, ' -> ', curr.period);
      return false;
    }
    if (prev.selectedMetric !== curr.selectedMetric) {
      console.log('Изменился selectedMetric:', prev.selectedMetric, ' -> ', curr.selectedMetric);
      return false;
    }
    if (prev.businessId !== curr.businessId) {
      console.log('Изменился businessId');
      return false;
    }
    
    // Сравнение диапазонов дат
    if (prev.dateRange && curr.dateRange) {
      // Проверяем существование дат и их валидность
      const prevStartValid = prev.dateRange.startDate instanceof Date && 
                            !isNaN(prev.dateRange.startDate.getTime());
      const prevEndValid = prev.dateRange.endDate instanceof Date && 
                          !isNaN(prev.dateRange.endDate.getTime());
      const currStartValid = curr.dateRange.startDate instanceof Date && 
                            !isNaN(curr.dateRange.startDate.getTime());
      const currEndValid = curr.dateRange.endDate instanceof Date && 
                          !isNaN(curr.dateRange.endDate.getTime());
      
      // Если какая-то из дат недействительна, считаем, что параметры изменились
      if (!prevStartValid || !prevEndValid || !currStartValid || !currEndValid) {
        console.log('Одна из дат недействительна');
        return false;
      }
      
      // Сравниваем даты
      if (prev.dateRange.startDate.getTime() !== curr.dateRange.startDate.getTime()) {
        console.log('Изменилась начальная дата:', 
          prev.dateRange.startDate.toISOString(), ' -> ', 
          curr.dateRange.startDate.toISOString());
        return false;
      }
      
      if (prev.dateRange.endDate.getTime() !== curr.dateRange.endDate.getTime()) {
        console.log('Изменилась конечная дата:', 
          prev.dateRange.endDate.toISOString(), ' -> ', 
          curr.dateRange.endDate.toISOString());
        return false;
      }
    } else if ((prev.dateRange && !curr.dateRange) || (!prev.dateRange && curr.dateRange)) {
      console.log('Изменилось наличие dateRange');
      return false;
    }
    
    console.log('Все параметры одинаковые');
    return true;
  };

  // Функция для преобразования часа из UTC в локальное время бизнеса
  const convertHourToBusinessTimezone = useCallback((utcHour: number, businessTimezone: string): number => {
    try {
      // Создаем объект DateTime в UTC с указанным часом
      const utcTime = DateTime.utc().set({ hour: utcHour, minute: 0, second: 0, millisecond: 0 });
      
      // Преобразуем в часовой пояс бизнеса
      const businessTime = utcTime.setZone(businessTimezone);
      
      return businessTime.hour;
    } catch (e) {
      console.error('Ошибка преобразования часа:', e);
      return utcHour; // В случае ошибки возвращаем исходный час
    }
  }, []);

  // Функция для преобразования пиковых часов из UTC в часовой пояс бизнеса
  const adjustPeakHoursTimezone = useCallback((peakHours: PeakHour[], businessTimezone: string) => {
    if (!peakHours || !Array.isArray(peakHours)) return [];
    
    // Создаем временный объект для хранения часов и их значений
    const hoursMap: Record<number, PeakHour> = {};
    
    // Преобразуем часы с учетом часового пояса
    peakHours.forEach(hourData => {
      if (typeof hourData.hour === 'number') {
        // Применяем смещение часового пояса к часу
        const businessHour = convertHourToBusinessTimezone(hourData.hour, businessTimezone);
        
        // Сохраняем или объединяем данные для этого локального часа
        if (hoursMap[businessHour]) {
          // Если уже есть данные для этого часа, суммируем
          hoursMap[businessHour].count += hourData.count;
          hoursMap[businessHour].percentage += hourData.percentage;
        } else {
          // Создаем новую запись для этого часа
          hoursMap[businessHour] = { ...hourData, hour: businessHour };
        }
      }
    });
    
    // Преобразуем обратно в массив и сортируем по часам
    return Object.values(hoursMap).sort((a, b) => a.hour - b.hour);
  }, [convertHourToBusinessTimezone]);

  const fetchDashboardData = useCallback(async () => {
    // Увеличиваем счетчик запросов для отладки
    fetchCounter.current += 1;
    const requestId = fetchCounter.current;
    
    console.log(`[${requestId}] Запрос данных дашборда начат`);
    
    if (isBusinessIdLoading || !businessId || !isMounted.current) {
      console.log(`[${requestId}] Запрос отменен: businessId загружается или компонент размонтирован`);
      return;
    }

    // Проверяем валидность dateRange
    const isDateRangeValid = dateRange && 
      dateRange.startDate instanceof Date && !isNaN(dateRange.startDate.getTime()) &&
      dateRange.endDate instanceof Date && !isNaN(dateRange.endDate.getTime());

    // Формируем текущие параметры
    const currentParams = { 
      period, 
      selectedMetric, 
      dateRange: isDateRangeValid ? dateRange : undefined,
      businessId 
    };
    
    // Сохраняем последнюю конфигурацию
    console.log(`[${requestId}] Текущие параметры:`, {
      period,
      selectedMetric,
      dateRange: isDateRangeValid ? {
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString()
      } : undefined,
      isFirstRender: isFirstRender.current
    });
    
    // Если не первый рендер, проверяем изменение параметров
    if (!isFirstRender.current) {
      console.log(`[${requestId}] Проверка изменения параметров`);
      
      // Если параметры не изменились, пропускаем запрос
      if (areParamsEqual(prevParams.current, currentParams)) {
        console.log(`[${requestId}] Параметры не изменились, пропускаем запрос`);
        return;
      }
    }
    
    // Сбрасываем флаг первого рендера
    isFirstRender.current = false;
    
    setIsLoading(true);
    setError(null);

    try {
      // Формируем параметры запроса
      const params: any = { 
        business: businessId.toString(),
        period: period,
        // Отправляем часовой пояс клиента, но на бэкенде будет использоваться часовой пояс бизнеса
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      // Проверка, что период валидный
      if (!['day', 'week', 'month', 'quarter', 'year', 'custom'].includes(period)) {
        console.warn(`Некорректный период: ${period}, используем 'day' по умолчанию`);
        params.period = 'day';
      }

      // Если есть диапазон дат и он валидный, добавляем его
      if (isDateRangeValid) {
        // Отправляем ISO строки для корректной обработки на бэкенде
        params.start_date = dateRange.startDate.toISOString();
        params.end_date = dateRange.endDate.toISOString();
        
        console.log(`Отправляем даты: start=${params.start_date}, end=${params.end_date}, timezone=${params.timezone}`);
      } else if (params.period === 'custom') {
        // Для custom периода обязательно нужны даты
        console.warn('Для периода custom требуется валидный dateRange, используем period=day');
        params.period = 'day';
      }
      
      console.log(`[${requestId}] Запрос к API с параметрами:`, params);
      
      // Сбрасываем данные перед новым запросом для избежания неправильного отображения
      setChartData([]);
      setRawData([]);
      setPeakHours([]);
      setPeakDays([]);
      setPlatformDistribution([]);
      
      const dashboardResponse = await api.get(`/api/dashboard/`, params);

      // Проверяем активность компонента
      if (!isMounted.current) {
        console.log(`[${requestId}] Компонент размонтирован, отменяем обработку ответа`);
        return;
      }

      console.log(`[${requestId}] Получен ответ от API`, 
        dashboardResponse ? 'с данными' : 'без данных');

      // Обновляем prevParams только после успешного запроса
      prevParams.current = currentParams;

      // Для отладки сохраняем сырые данные
      if (dashboardResponse && dashboardResponse.orders_by_month) {
        setRawData(dashboardResponse.orders_by_month);
      }

      // Получаем часовой пояс бизнеса
      const responseTz = dashboardResponse?.timezone || dashboardResponse?.business_timezone || 'UTC';
      setBusinessTimezone(responseTz);

      // Обновляем метрики и данные графика
      if (dashboardResponse && dashboardResponse.metrics) {
        setMetrics(dashboardResponse.metrics);
        
        // Используем chart_data в приоритете, если они есть
        const chartDataSource = dashboardResponse.chart_data || dashboardResponse.orders_by_month || [];
        
        console.log(`[${requestId}] Обработка данных графика, источник данных:`, 
          chartDataSource ? `${chartDataSource.length} записей` : 'нет данных');
        
        // Получаем часовой пояс из ответа (если есть)
        const responseTimezone = dashboardResponse.timezone;
        
        // Используем общий процессор для обработки данных графика с передачей часового пояса из ответа
        const processedChartData = processChartData(
          chartDataSource, 
          params.period, // Используем именно тот период, который был отправлен в запросе
          selectedMetric,
          t,
          responseTimezone // Передаем часовой пояс из ответа API (если есть)
        );
        
        console.log(`[${requestId}] Обработанные данные графика:`, 
          processedChartData ? `${processedChartData.length} записей` : 'нет данных');
        
        setChartData(processedChartData);

        // Обрабатываем новые данные для компонентов
        if (dashboardResponse.peak_hours) {
          // Обрабатываем пиковые часы с учетом часового пояса
          const adjustedHours = adjustPeakHoursTimezone(
            dashboardResponse.peak_hours, 
            responseTimezone
          );
          setPeakHours(adjustedHours);
          console.log('Пиковые часы после преобразования:', adjustedHours);
        }

        if (dashboardResponse.peak_days) {
          setPeakDays(dashboardResponse.peak_days);
        }

        if (dashboardResponse.platform_distribution) {
          setPlatformDistribution(dashboardResponse.platform_distribution);
        }
      } else if (dashboardResponse) {
        console.log(`[${requestId}] Используем старый формат ответа API`);
        
        // Сохраняем совместимость со старым форматом ответа
        const processedMetrics = processMetrics(dashboardResponse, dashboardResponse);
        setMetrics(processedMetrics);
        
        // Обрабатываем данные для графика с использованием общего процессора
        const processedChartData = processChartData(
          dashboardResponse.orders_by_month || [], 
          params.period, // Используем именно тот период, который был отправлен в запросе
          selectedMetric,
          t,
          dashboardResponse.timezone // Передаем часовой пояс из ответа API (если есть)
        );
        
        setChartData(processedChartData);

        // Обрабатываем новые данные для компонентов, если они есть
        if (dashboardResponse.peak_hours) {
          // Обрабатываем пиковые часы с учетом часового пояса
          const adjustedHours = adjustPeakHoursTimezone(
            dashboardResponse.peak_hours, 
            dashboardResponse.timezone
          );
          setPeakHours(adjustedHours);
          console.log('Пиковые часы после преобразования (старый формат):', adjustedHours);
        }

        if (dashboardResponse.peak_days) {
          setPeakDays(dashboardResponse.peak_days);
        }

        if (dashboardResponse.platform_distribution) {
          setPlatformDistribution(dashboardResponse.platform_distribution);
        }
      } else {
        console.log(`[${requestId}] Нет данных в ответе API`);
        setError(t('errors:dashboard_loading_error'));
      }
    } catch (error: any) {
      if (isMounted.current) {
        console.error(`[${requestId}] Ошибка загрузки данных:`, error);
        setError(error.message || t('errors:dashboard_loading_error'));
      }
    } finally {
      if (isMounted.current) {
        console.log(`[${requestId}] Завершение запроса`);
        setIsLoading(false);
      }
    }
  }, [businessId, isBusinessIdLoading, period, selectedMetric, dateRange, t, adjustPeakHoursTimezone]);

  // Эффект для инициализации и обновления данных
  useEffect(() => {
    console.log('Монтирование компонента, настройка параметров');
    isMounted.current = true;
    
    // Запускаем первичную загрузку данных
    fetchDashboardData();
    
    return () => {
      console.log('Размонтирование компонента');
      isMounted.current = false;
    };
  }, [fetchDashboardData]);

  // Функции обновления состояния
  const updatePeriod = useCallback((newPeriod: Period) => {
    console.log('updatePeriod вызван:', newPeriod);
    if (newPeriod !== period) {
      // Явно сбрасываем данные при изменении периода
      setChartData([]);
      setRawData([]);
      setPeakHours([]);
      setPeakDays([]);
      setPlatformDistribution([]);
      setPeriod(newPeriod);
    }
  }, [period]);

  const updateSelectedMetric = useCallback((newMetric: MetricType) => {
    console.log('updateSelectedMetric вызван:', newMetric);
    if (newMetric !== selectedMetric) {
      setSelectedMetric(newMetric);
    }
  }, [selectedMetric]);

  const updateDateRange = useCallback((range: DateRange) => {
    console.log('updateDateRange вызван:', {
      startDate: range.startDate.toISOString(),
      endDate: range.endDate.toISOString()
    });
    
    // Проверка на валидность дат
    const isStartDateValid = range.startDate instanceof Date && !isNaN(range.startDate.getTime());
    const isEndDateValid = range.endDate instanceof Date && !isNaN(range.endDate.getTime());
    
    if (!isStartDateValid || !isEndDateValid) {
      console.error('Получены невалидные даты:', range);
      return;
    }
    
    // Проверка на изменение диапазона дат
    const isNewRange = !dateRange || 
        range.startDate.getTime() !== dateRange.startDate.getTime() || 
        range.endDate.getTime() !== dateRange.endDate.getTime();
    
    if (isNewRange) {
      console.log('Диапазон дат изменился, обновляем');
      // Сбрасываем данные при изменении диапазона дат
      setChartData([]);
      setRawData([]);
      setPeakHours([]);
      setPeakDays([]);
      setPlatformDistribution([]);
      setDateRange(range);
    } else {
      console.log('Диапазон дат не изменился, пропускаем обновление');
    }
  }, [dateRange]);

  return {
    metrics,
    chartData,
    period,
    selectedMetric,
    isLoading,
    error,
    rawData,
    dateRange,
    // Новые данные
    peakHours,
    peakDays,
    platformDistribution,
    businessTimezone,
    // Методы
    setPeriod: updatePeriod,
    setSelectedMetric: updateSelectedMetric,
    setDateRange: updateDateRange,
    refreshDashboard: fetchDashboardData
  };
};

// Вспомогательная функция для обработки метрик
function processMetrics(
  analyticsData: any, 
  businessStatsData: any
): DashboardMetrics {
  return {
    revenue: calculateMetric(
      businessStatsData.total_revenue, 
      analyticsData.revenue_change
    ),
    averageCheck: calculateMetric(
      analyticsData.average_order_value, 
      analyticsData.average_check_change
    ),
    orders: calculateMetric(
      businessStatsData.orders_count, 
      analyticsData.orders_change
    ),
    clients: calculateMetric(
      businessStatsData.clients_count, 
      analyticsData.clients_change
    ),
    conversion: calculateMetric(
      analyticsData.conversion_rate, 
      analyticsData.conversion_change
    )
  };
}

function calculateMetric(value: number, change: number): Metric {
  return {
    value,
    change,
    trend: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral'
  };
}